package com.eladfinish.bible.contest.navigation

import androidx.compose.runtime.Composable
import androidx.navigation.NavType
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.app_name
import com.eladfinish.bible.contest.browse
import com.eladfinish.bible.contest.complete_chapter
import com.eladfinish.bible.contest.complete_verse
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetEntryPoint
import com.eladfinish.bible.contest.lexicographical_sort_preview_title
import com.eladfinish.bible.contest.lexicographically
import com.eladfinish.bible.contest.mikud
import com.eladfinish.bible.contest.multiple_choice_questions
import com.eladfinish.bible.contest.navigation.NavigationDestination.Companion.Companion.createTypeMap
import com.eladfinish.bible.contest.open_questions
import com.eladfinish.bible.contest.pair_matching
import com.eladfinish.bible.contest.past_quizzes
import com.eladfinish.bible.contest.pyramid_crossword
import com.eladfinish.bible.contest.screen_misc
import com.eladfinish.bible.contest.screen_names_in_bible
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BrowseMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.ShonotEntry
import com.eladfinish.bible.contest.screens.settings.debug.DEBUG_FLAGS_TITLE
import com.eladfinish.bible.contest.search_in_bible
import com.eladfinish.bible.contest.seminar_game_bible_contest
import com.eladfinish.bible.contest.title_activity_settings
import com.eladfinish.bible.contest.title_random_verse
import com.eladfinish.bible.contest.verse_widget
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.serialization.Serializable
import kotlin.reflect.KType

@Serializable
data object RootNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.app_name)

    override val screenName: String = "Root Navigation Destination"
}

@Serializable
data object NamesScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.screen_names_in_bible)

    override val screenName: String = "Names Screen Navigation Destination"
}

@Serializable
data object SettingsRootNavigationDestination : NavigationDestination

@Serializable
data object SettingsScreenNavigationDestination : NavigationDestination {
    override val title: String
        @Composable get() = stringResource(SharedRes.strings.title_activity_settings)

    override val screenName: String = "Settings Screen Navigation Destination"
}

/** Debug builds only: toggles for the boolean flags kept in Settings. */
@Serializable
data object DebugFlagsScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = DEBUG_FLAGS_TITLE

    override val screenName: String = "Debug Flags Screen Navigation Destination"
}

@Serializable
data object PyramidScreenNavigationDestination : NavigationDestination {
    override val title: String
        @Composable get() = stringResource(SharedRes.strings.pyramid_crossword)

    override val screenName: String = "Pyramid Screen Navigation Destination"
}

@Serializable
data object MiscScreenNavigationDestination : NavigationDestination {
    override val title: String
        @Composable get() = stringResource(SharedRes.strings.screen_misc)

    override val screenName: String = "Misc Screen Navigation Destination"
}

@Serializable
data class VerseWidgetScreenNavigationDestination(
    /** What opened the screen, for the analytics: the tile, the "what's new" sheet, the widget's gear... */
    val entryPoint: VerseWidgetEntryPoint = VerseWidgetEntryPoint.HOME_TILE,
) : NavigationDestination {
    override val title: String
        @Composable get() = stringResource(SharedRes.strings.verse_widget)

    override val screenName: String = "Verse Widget Screen Navigation Destination"

    /**
     * The entry point is an enum. Navigation finds the type of an enum argument by reflection, which only the JVM
     * has: without this map the destination cannot be added to the graph on iOS and web, and the app closes at launch.
     */
    companion object : NavigationDestination.Companion {
        override val typeMap: Map<KType, NavType<*>> = createTypeMap(VerseWidgetEntryPoint.serializer())
    }
}

@Serializable
data object MonopolyScreenNavigationDestination : NavigationDestination {
    override val title: String
        @Composable get() = stringResource(SharedRes.strings.seminar_game_bible_contest)

    override val screenName: String = "Monopoly Screen Navigation Destination"
}

@Serializable
data object RandomVerseScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.title_random_verse)

    override val screenName: String = "Random Verse Screen Navigation Destination"
}

@Serializable
data object CompleteVerseScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.complete_verse)

    override val screenName: String = "Complete Verse Screen Navigation Destination"
}

@Serializable
data class SearchScreenNavigationDestination(
    val text: String = "",
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.search_in_bible)

    override val screenName: String = "Search Screen Navigation Destination"
}

@Serializable
data object CompleteChapterScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.complete_chapter)

    override val screenName: String = "Complete Chapter Screen Navigation Destination"
}

@Serializable
data object PairMatchingScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.pair_matching)

    override val screenName: String = "Pair Matching Screen Navigation Destination"
}

@Serializable
data class MultipleChoiceQuestionsScreenNavigationDestination(
    val fromPairMatching: Boolean = false,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.multiple_choice_questions)

    override val screenName: String = "Multiple Choice Questions Screen Navigation Destination"
}

@Serializable
data class OpenQuestionsScreenNavigationDestination(
    val isMultipleRandomVersesMode: Boolean = false,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.open_questions)

    override val screenName: String = "Open Questions V2 Screen Navigation Destination"
}

@Serializable
data class BrowseScreenNavigationDestination(
    val fromDialog: Boolean = false,
    val bookIndex: Int = -1,
    val chapterIndex: Int = -1,
    /** The 1-based verse to scroll to and highlight once the chapter is open, or 0 for none. */
    val verse: Int = 0,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.browse)

    override val screenName: String = "Browse Screen Navigation Destination"
}

@Serializable
data class ViewContentTabsScreenNavigationDestination(
    val browseMode: String,
    val isCompleteChapter: Boolean = false,
    val indexPickedBook: Int,
    val indexPickedPart: Int = -1,
    val bookmarkScrollY: Int = -1,
    val bookmarkTextOffset: Int = -1,
    val completionDetails: String? = null,
    val showOnqelos: Boolean = false,
    /** The 1-based verse to scroll to and highlight once the chapter is open, or 0 for none. */
    val verse: Int = 0,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.browse)

    override val screenName: String = "View Content Tabs Screen Navigation Destination"
}

/**
 * An entry of the "שונות" tab of Browse ([entry] is its [ShonotEntry.slug]), among the tabs of the
 * others: the chapter screen of [BrowseMode.SHONOT_MODE].
 */
@Serializable
data class ShonotScreenNavigationDestination(
    val entry: String = ShonotEntry.ROSH_CHODESH.slug,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.browse)

    override val screenName: String = "Shonot Screen Navigation Destination"
}

@Serializable
data object MikudScreenNavigationDestination : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.mikud)

    override val screenName: String = "Mikud Screen Navigation Destination"
}

/** [duelCode]: a ראש בראש room to open (a `/duel/{code}` link), or null for the exam's home page. */
@Serializable
data class PastQuizzesNavigationDestination(
    val duelCode: String? = null,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.past_quizzes)

    override val screenName: String = "Past Quizzes Navigation Destination"
}

@Serializable
data class LexicographicalSortScreenNavigationDestination(
    val bookIndex: Int = -1,
    val chapterIndex: Int = -1,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.lexicographically)

    override val screenName: String = "Lexicographical Sort Screen Navigation Destination"
}

@Serializable
data class LexicographicalSortPdfViewerNavigationDestination(
    val bookIndex: Int,
    val chapterIndex: Int,
    val outputChaptersOrdinal: Int,
) : NavigationDestination {

    override val title: String
        @Composable get() = stringResource(SharedRes.strings.lexicographical_sort_preview_title)

    override val screenName: String = "Lexicographical Sort PDF Viewer Navigation Destination"
}
